import { Box, Button, Card, Checkbox, Grid, TextField, Typography, Select, MenuItem, IconButton, InputLabel, FormControl, FormControlLabel, Radio, RadioGroup, Chip, Alert, FormHelperText, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
// Type Icons for static fields
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import NotesIcon from '@mui/icons-material/Notes';
import NumbersIcon from '@mui/icons-material/Numbers';
import WDBox from "../../../../MaterialComponents/WDBox";
import WDButton from "../../../../MaterialComponents/WDButton";
import WDTypography from "../../../../MaterialComponents/WDTypography";
import AppService from "../../../../Configurations/Appservice";

import { Formik, Form, Field } from "formik";
import { object, string } from "yup";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  dismissLoader,
  showLoader,
  showErroMessage,
  showSuccessMessage,
  updateScreenTitle,
} from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import { STATIC_FIELDS, getFieldsByTargetType, ALL_CATEGORY_DISPLAY_NAMES } from "../../Service/NewService/Data";
import WdDivider from "../../../../Components/WdDivider";
import pxToRem from "../../../../Themes/Functions/pxToRem";
import ServiceField from "./panels/ServiceField";
import uuid from "react-uuid";
import _ from "lodash";
import LayoutCard from "../../../../Components/LayoutCard";
import SubTitle from "../../../../Components/SubTitle";
import * as fieldCategoryUtils from "../../../../utils/fieldCategoryUtils";
import IconSelector from "../../../../Components/IconSelector";

const cardStyle = {
  marginBottom: '16px',
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  overflow: 'hidden'
};

const headerStyle = {
  backgroundColor: '#009688',
  color: '#fff',
  padding: '8px 16px',
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontSize: '0.875rem',
  fontWeight: 500,
  letterSpacing: '0.1px',
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '4px'
};

const cardContentStyle = {
  padding: '16px'
};

const initialValues = {
  name: "",
};

const validationSchema = object({
  name: string().required("Name is Mandatory"),
});

const initialField = {
  type: "TEXT",  // Default to TEXT instead of null
  title: "",     // Empty string instead of null
  mandatory: false,
  category: "SERVICE_SPECIFIC" // Add default category
};

// Helper to get icon for field type
const getFieldTypeIcon = (type) => {
  const iconStyle = { fontSize: 12, color: '#757575' };
  switch (type) {
    case 'DATE':
      return <CalendarTodayIcon sx={iconStyle} />;
    case 'FILE':
      return <AttachFileIcon sx={iconStyle} />;
    case 'EMAIL':
      return <EmailIcon sx={iconStyle} />;
    case 'PHONE':
      return <PhoneIcon sx={iconStyle} />;
    case 'SELECT':
      return <ArrowDropDownCircleIcon sx={iconStyle} />;
    case 'TEXTAREA':
      return <NotesIcon sx={iconStyle} />;
    case 'NUMBER':
      return <NumbersIcon sx={iconStyle} />;
    case 'TEXT':
    default:
      return <TextFieldsIcon sx={iconStyle} />;
  }
};

// Target type options for service groups
const TARGET_TYPES = [
  { value: 'EMPLOYEE', label: 'Employee Services', icon: <PersonIcon />, description: 'Services for individual employees (Visa, Labor Card, etc.)' },
  { value: 'ORGANIZATION', label: 'Organization Services', icon: <BusinessIcon />, description: 'Services for the company itself (Trade License, Tax Filing, etc.)' },
  { value: 'BOTH', label: 'Both', icon: <GroupsIcon />, description: 'Services applicable to both employees and organization' },
];

const NewServiceGroup = () => {
  const [staticFields, setStaticFields] = useState({});
  // Start with no dynamic field rows; users add them via "Add More Fields".
  const [fields, setFields] = useState([]);
  const [categorizedFields, setCategorizedFields] = useState({});
  const [availableActions, setAvailableActions] = useState([]); // fetched from API
  const [selectedActionId, setSelectedActionId] = useState(""); // currently selected in dropdown
  // Selected actions include both existing actions and newly created ones.
  // For existing: { actionId, name, description, dueDays, isNew: false }
  // For new: { tempId, name, description, dueDays, isNew: true }
  const [selectedActions, setSelectedActions] = useState([]);
  const [newActionsAttemptedSubmit, setNewActionsAttemptedSubmit] = useState(false);
  const [pendingActionsFromDetails, setPendingActionsFromDetails] = useState(null); // holds actions array from details until we have availableActions

  // NEW: Service Group Tags
  const [targetType, setTargetType] = useState('EMPLOYEE'); // Default to EMPLOYEE
  // NEW: Icon and Color for Service Group
  const [icon, setIcon] = useState(null);
  const [color, setColor] = useState(null);

  const location = useLocation();
  const dispatch = useDispatch();
  const formikRef = useRef();

  // useEffect(() => {
  //   setStaticFields(_.cloneDeep(STATIC_FIELDS));
  // }, []);

  useEffect(() => {
    const group = location.state?.group;
    if (group) {
      dispatch(updateScreenTitle("Edit Service Group"));
      _loadFieldData(group);
    } else {
      dispatch(updateScreenTitle("New Service Group"));
      // Initialize with filtered fields based on default targetType
      _initializeStaticFields(targetType);
    }
    // Fetch actions list on mount
    AppService.get("v1/action")
      .then((actions) => {
        // Expecting array with id, name, description, defaultDueDays
        setAvailableActions(Array.isArray(actions) ? actions : []);
      })
      .catch(() => {
        setAvailableActions([]);
      });
    return () => dispatch(updateScreenTitle(""));
  }, []);

  // Update static fields when target type changes
  useEffect(() => {
    const group = location.state?.group;
    if (!group) {
      // Only re-initialize if not in edit mode
      _initializeStaticFields(targetType);
    }
  }, [targetType]);

  // Helper to initialize static fields based on target type
  const _initializeStaticFields = (type) => {
    const filteredFields = getFieldsByTargetType(type);
    const statFieldsCopy = _.cloneDeep(filteredFields);

    // Set default checked field based on target type
    if (type === 'ORGANIZATION' || type === 'BOTH') {
      if (statFieldsCopy?.companyNameEn) {
        statFieldsCopy.companyNameEn.checked = true;
      }
    }
    if (type === 'EMPLOYEE' || type === 'BOTH') {
      // nameEn is always-on (required); firstNameEn starts checked but can be unticked.
      if (statFieldsCopy?.nameEn) {
        statFieldsCopy.nameEn.checked = true;
      }
      if (statFieldsCopy?.firstNameEn) {
        statFieldsCopy.firstNameEn.checked = true;
      }
    }

    setStaticFields(statFieldsCopy);
  };

  // When availableActions or pendingActionsFromDetails changes, reconcile to selectedActions in edit mode
  useEffect(() => {
    if (!pendingActionsFromDetails) return;

    let reconciled = [];
    if (Array.isArray(availableActions) && availableActions.length > 0) {
      reconciled = pendingActionsFromDetails.map((pa) => {
        // Try to match by exact name and description
        const match = availableActions.find(a => a.name === pa.name && a.description === pa.description);
        if (match) {
          return {
            actionId: match.id,
            name: match.name,
            description: match.description,
            dueDays: pa.dueDays ?? match.defaultDueDays ?? 0,
            isNew: false,
          };
        }
        // If not found, treat as a new action row
        return {
          tempId: `new-${uuid()}`,
          name: pa.name || "",
          description: pa.description || "",
          dueDays: pa.dueDays ?? 0,
          isNew: true,
        };
      });
    } else {
      // No available actions list; import all as new rows so user can save them
      reconciled = pendingActionsFromDetails.map((pa) => ({
        tempId: `new-${uuid()}`,
        name: pa.name || "",
        description: pa.description || "",
        dueDays: pa.dueDays ?? 0,
        isNew: true,
      }));
    }

    setSelectedActions(reconciled);
    setPendingActionsFromDetails(null);
  }, [availableActions, pendingActionsFromDetails]);

  // Add useEffect to run categorizeFields when fields or staticFields change
  useEffect(() => {
    if (Object.keys(staticFields).length > 0) {
      categorizeFields();
    }
  }, [fields, staticFields]);

  const _loadFieldData = (group) => {
    dispatch(showLoader());
    AppService.get(`v1/serviceGroup/details?groupId=${group.id}`)
      .then((details) => {
        setFields(details.fields);

        // Load targetType
        let loadedTargetType = details.targetType || 'EMPLOYEE';
        setTargetType(loadedTargetType);

        // Get filtered fields based on loaded targetType
        const filteredFields = getFieldsByTargetType(loadedTargetType);
        let statFieldsCopy = _.cloneDeep(filteredFields);

        // Mark previously selected static fields as checked
        if (Array.isArray(details.staticFields)) {
          details.staticFields.forEach((code) => {
            if (statFieldsCopy[code]) {
              statFieldsCopy[code].checked = true;
            }
          });
        }

        // Set required field based on target type
        if (loadedTargetType === 'EMPLOYEE' || loadedTargetType === 'BOTH') {
          // nameEn is the required always-on field; firstNameEn keeps whatever was saved (untickable).
          if (statFieldsCopy?.nameEn) {
            statFieldsCopy.nameEn.checked = true;
          }
        }
        if (loadedTargetType === 'ORGANIZATION' || loadedTargetType === 'BOTH') {
          if (statFieldsCopy?.companyNameEn) {
            statFieldsCopy.companyNameEn.checked = true;
          }
        }

        setStaticFields(statFieldsCopy);
        formikRef.current.setFieldValue("name", details.name);

        // Load icon and color if available
        if (details.icon) {
          setIcon(details.icon);
        }
        if (details.color) {
          setColor(details.color);
        }

        // Save actions from details to reconcile later once availableActions is loaded
        if (Array.isArray(details.actions)) {
          setPendingActionsFromDetails(details.actions);
        }
      })
      .catch(() => {})
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  const _onSubmit = (values, { resetForm }) => {
    // Validate new actions (required: name, description, dueDays)
    const isNewActionsValid = selectedActions.every(a => !a.isNew || (
      Boolean(a.name && a.name.trim()) &&
      Boolean(a.description && a.description.trim()) &&
      a.dueDays !== '' && !isNaN(Number(a.dueDays))
    ));
    if (!isNewActionsValid) {
      setNewActionsAttemptedSubmit(true);
      return;
    }
    // Validate dynamic form fields: every added row must have a title.
    const hasUntitledField = fields.some((f) => !f.title || !f.title.trim());
    if (hasUntitledField) {
      dispatch(showErroMessage("Please enter a title for every form field, or remove the empty row."));
      return;
    }
    const group = location.state?.group;
    if (group) {
      _update(values, { resetForm });
    } else {
      _create(values, { resetForm });
    }
  };

  const _create = (values, { resetForm }) => {
    dispatch(showLoader());

    let staticFieldsCopy = { ...staticFields };
    const staticFieldsAdded = Object.keys(staticFieldsCopy).filter(
      (key) => staticFieldsCopy[key].checked
    );

    // Split payload into existing actions and newly created actions
    const existingActions = selectedActions
      .filter(a => !a.isNew)
      .map(a => ({ actionId: a.actionId, dueDays: Number(a.dueDays) }));
    const newActions = selectedActions
      .filter(a => a.isNew)
      .map(a => ({ name: a.name?.trim(), description: a.description?.trim(), defaultDueDays: Number(a.dueDays) }));

    let body = {
      ...values,
      fields: [...fields],
      staticFields: staticFieldsAdded,
      actions: existingActions,
      newActions,
      targetType,
      icon: icon || null,
      color: color || null,
    };

    AppService.post("v1/serviceGroup", body)
      .then((response) => {
        dispatch(showSuccessMessage("Group Created Successfully"));
        resetForm();
        let statFieldsCopy = _.cloneDeep(STATIC_FIELDS);
        if (statFieldsCopy?.nameEn) {
          statFieldsCopy.nameEn.checked = true;
        }
        if (statFieldsCopy?.firstNameEn) {
          statFieldsCopy.firstNameEn.checked = true;
        }
        console.log(statFieldsCopy);
        setStaticFields(statFieldsCopy);
        setSelectedActions([]);
        setNewActionsAttemptedSubmit(false);
        // Reset tag selections
        setTargetType('EMPLOYEE');
        // Reset icon and color
        setIcon(null);
        setColor(null);
      })
      .catch((error) => {})
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  const _update = (values, { resetForm }) => {
    dispatch(showLoader());

    let staticFieldsCopy = { ...staticFields };
    const staticFieldsAdded = Object.keys(staticFieldsCopy).filter(
      (key) => staticFieldsCopy[key].checked
    );

    const group = location.state?.group;
    // Split payload into existing actions and newly created actions
    const existingActions = selectedActions
      .filter(a => !a.isNew)
      .map(a => ({ actionId: a.actionId, dueDays: Number(a.dueDays) }));
    const newActions = selectedActions
      .filter(a => a.isNew)
      .map(a => ({ name: a.name?.trim(), description: a.description?.trim(), defaultDueDays: Number(a.dueDays) }));

    let body = {
      ...values,
      id: group.id,
      fields: [...fields],
      staticFields: staticFieldsAdded,
      actions: existingActions,
      newActions,
      targetType,
      icon: icon || null,
      color: color || null,
    };

    AppService.put("v1/serviceGroup", body)
      .then((response) => {
        dispatch(showSuccessMessage("Group Updated Successfully"));
        resetForm();
        setNewActionsAttemptedSubmit(false);
      })
      .catch((error) => {})
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  const _onStatFieldSelect = (fieldCode, event) => {
    let statFieldsCopy = { ...staticFields };
    // nameEn is always selected and cannot be unticked; firstNameEn starts checked but is untickable.
    if (fieldCode === "nameEn") {
      statFieldsCopy[fieldCode].checked = true;
    } else {
      statFieldsCopy[fieldCode].checked = event;
    }
    setStaticFields(statFieldsCopy);
    formikRef.current.validateForm();
  };
  
  // Auto-categorize static fields without showing dropdowns
  const _autoCategorizeStaticFields = () => {
    const statFieldsCopy = { ...staticFields };
    
    // Automatically categorize fields based on their names using fieldCategoryUtils
    Object.keys(statFieldsCopy).forEach(key => {
      // Create a field object in the format expected by the categorizeField function
      const fieldObj = {
        name: key,
        label: statFieldsCopy[key].label || key,
        type: statFieldsCopy[key].type || 'TEXT',
      };
      
      // Use the utility function to categorize the field
      const category = fieldCategoryUtils.categorizeField(fieldObj);
      
      // Store the category in the field
      statFieldsCopy[key].category = category;
      
      // Log for debugging
      console.log(`Auto-categorized ${key} as ${category}`);
    });
    
    return statFieldsCopy;
  };

  // Function to categorize fields for preview
  const categorizeFields = () => {
    // Auto-categorize static fields
    const autoCategorizedFields = _autoCategorizeStaticFields();
    setStaticFields(autoCategorizedFields); // Update the state with categorized fields
    
    // Convert static fields to the format expected by CategorizedFields
    const staticFieldsArray = Object.keys(autoCategorizedFields)
      .filter(key => autoCategorizedFields[key].checked)
      .map(key => {
        const field = autoCategorizedFields[key];
        return {
          id: key,
          name: key,
          label: field.label || key,
          type: field.type || 'TEXT',
          category: field.category, // Use the auto-categorized value
          mandatory: field.mandatory || false,
          isStatic: true
        };
      });
    
    // Convert dynamic fields to the format expected by CategorizedFields
    const dynamicFieldsArray = fields
      .filter(field => field.title && field.type) // Only include fields with a title and type
      .map(field => {
        return {
          id: field.id,
          name: field.id,
          label: field.title || 'Untitled Field',
          type: field.type || 'TEXT',
          category: field.category || 'SERVICE_SPECIFIC', // Use the field's explicit category if available
          mandatory: field.mandatory || false,
          isStatic: false
        };
      });
    
    // Combine both sets of fields
    const allFields = [...staticFieldsArray, ...dynamicFieldsArray];
    
    // Group fields by category
    const groupedFields = {};
    allFields.forEach(field => {
      const category = field.category || 'SERVICE_SPECIFIC';
      if (!groupedFields[category]) {
        groupedFields[category] = [];
      }
      groupedFields[category].push(field);
    });
    
    setCategorizedFields(groupedFields);
  };

  // Function to render the categorized field preview
  const renderCategorizedFieldsPreview = () => {
    if (!categorizedFields || Object.keys(categorizedFields).length === 0) {
      return null;
    }
    
    // Define category display order with friendly names
    const categoryConfig = [
      { key: 'PERSONAL_INFO', displayName: 'Personal Information' },
      { key: 'CONTACT_INFO', displayName: 'Contact Information' },
      { key: 'IDENTIFICATION', displayName: 'Identification' },
      { key: 'ADDRESS', displayName: 'Address Details' },
      { key: 'DOCUMENTS', displayName: 'Required Documents' },
      { key: 'SERVICE_SPECIFIC', displayName: 'Service Information' },
      { key: 'OTHER', displayName: 'Additional Information' }
    ];
    
    // Create a mapping for display names
    const categoryDisplayNames = {};
    categoryConfig.forEach(config => {
      categoryDisplayNames[config.key] = config.displayName;
    });
    
    // Get the order of categories
    const categoryOrder = categoryConfig.map(config => config.key);
    
    // Sort category keys based on the defined order
    const sortedCategoryKeys = Object.keys(categorizedFields).sort((a, b) => {
      const indexA = categoryOrder.indexOf(a);
      const indexB = categoryOrder.indexOf(b);
      
      // If both categories are in our order list, use that order
      if (indexA >= 0 && indexB >= 0) {
        return indexA - indexB;
      }
      
      // If only one category is in our list, that one comes first
      if (indexA >= 0) return -1;
      if (indexB >= 0) return 1;
      
      // Otherwise, sort alphabetically
      return a.localeCompare(b);
    });
    
    return (
      <Box sx={cardStyle} my={3}>
        <Box sx={headerStyle}>
          <WDTypography fontSize={16} variant="h6" color="white">
            Field Preview (Categorized)
          </WDTypography>
        </Box>
        <Box sx={cardContentStyle}>
          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            This is how your fields will be organized in forms:
          </Typography>
          
          {sortedCategoryKeys.map(category => {
            const fieldsForCategory = categorizedFields[category];
            // Skip empty categories
            if (!fieldsForCategory || fieldsForCategory.length === 0) {
              return null;
            }
            
            // Get the display name for this category
            const displayName = categoryDisplayNames[category] || 
              fieldCategoryUtils.CATEGORY_DISPLAY_NAMES[category] || 
              category;
            
            return (
              <Box key={category} sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                <Box sx={{ bgcolor: '#009688', px: 2, py: 1 }}>
                  <Typography variant="subtitle1" color="white">
                    {displayName}
                  </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    {fieldsForCategory.map(field => (
                      <Grid item xs={12} sm={6} md={4} key={field.id}>
                        <Box sx={{ 
                          p: 1, 
                          border: '1px solid #e0e0e0', 
                          borderRadius: '4px',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <Typography variant="subtitle2">{field.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Type: {field.type}
                            {field.mandatory && <span style={{ color: '#f44336', marginLeft: '4px' }}>*</span>}
                          </Typography>
                          {field.isStatic && (
                            <Typography variant="caption" color="primary.main" sx={{ mt: 1 }}>
                              Standard field
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const StaticFields = () => {
    // Group fields by category using explicit category from each field
    const groupedFields = {};
    Object.keys(staticFields).forEach(key => {
      const field = staticFields[key];
      const category = field.category || 'OTHER';

      if (!groupedFields[category]) {
        groupedFields[category] = [];
      }

      groupedFields[category].push({
        id: key,
        code: key,
        label: field.label,
        checked: field.checked,
        type: field.type || 'TEXT', // Field type for icon display
        targetType: field.targetType
      });
    });

    // Define category display order based on target type
    const getCategoryOrder = () => {
      const commonCategories = ['CONTACT_INFO', 'ADDRESS', 'SERVICE_SPECIFIC', 'OTHER'];
      const employeeCategories = ['PERSONAL_INFO', 'IDENTIFICATION', 'DOCUMENTS', 'LOCATIONS'];
      const orgCategories = ['COMPANY_INFO', 'LICENSE_INFO', 'GOVERNMENT_CARDS', 'TAX_INFO', 'FINANCIAL', 'SIGNATORY', 'OWNER_INFO', 'ORG_DOCUMENTS'];

      if (targetType === 'EMPLOYEE') {
        return [...commonCategories, ...employeeCategories];
      } else if (targetType === 'ORGANIZATION') {
        return [...commonCategories, ...orgCategories];
      } else {
        // BOTH - show all
        return [...commonCategories, ...employeeCategories, ...orgCategories];
      }
    };

    const categoryOrder = getCategoryOrder();

    // Sort category keys based on the defined order
    const sortedCategoryKeys = Object.keys(groupedFields).sort((a, b) => {
      const indexA = categoryOrder.indexOf(a);
      const indexB = categoryOrder.indexOf(b);

      if (indexA >= 0 && indexB >= 0) return indexA - indexB;
      if (indexA >= 0) return -1;
      if (indexB >= 0) return 1;
      return a.localeCompare(b);
    });

    // Get default required field based on target type
    const getRequiredField = () => {
      if (targetType === 'EMPLOYEE') return 'nameEn';
      if (targetType === 'ORGANIZATION') return 'companyNameEn';
      return null; // BOTH - no required field
    };
    const requiredField = getRequiredField();

    return (
      <Box my={3}>
        <SubTitle>
          Standard Fields
          <Chip
            label={targetType === 'EMPLOYEE' ? 'Employee' : targetType === 'ORGANIZATION' ? 'Organization' : 'All'}
            size="small"
            sx={{ ml: 1, height: 20, fontSize: 11 }}
            color={targetType === 'EMPLOYEE' ? 'primary' : targetType === 'ORGANIZATION' ? 'success' : 'warning'}
          />
        </SubTitle>
        {sortedCategoryKeys.map(category => {
          const fieldsForCategory = groupedFields[category];
          if (!fieldsForCategory || fieldsForCategory.length === 0) return null;

          const displayName = ALL_CATEGORY_DISPLAY_NAMES[category] || fieldCategoryUtils.CATEGORY_DISPLAY_NAMES[category] || category;

          return (
            <Box sx={cardStyle} my={2} key={category}>
              <Box sx={headerStyle}>
                <WDTypography fontSize={14} variant="h6" color="white">
                  {displayName}
                </WDTypography>
              </Box>
              <Box sx={{ ...cardContentStyle, py: 1.5 }}>
                <Grid container spacing={1}>
                  {fieldsForCategory.map(field => (
                    <Grid item xs={12} sm={6} md={3} key={field.code}>
                      <Tooltip title={`${field.label} (${field.type || 'TEXT'})`} placement="top" arrow>
                        <Box
                          sx={{
                            flexDirection: "row",
                            height: pxToRem(36),
                            border: 1,
                            borderColor: field.checked ? "primary.main" : "grey.300",
                            backgroundColor: field.checked ? "primary.50" : "transparent",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 1,
                            cursor: "pointer",
                            transition: "all 0.2s",
                            '&:hover': {
                              borderColor: "primary.main",
                              backgroundColor: "grey.50"
                            }
                          }}
                          onClick={() => {
                            if (field.code !== requiredField) {
                              _onStatFieldSelect(field.code, !field.checked);
                            }
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
                            {getFieldTypeIcon(field.type)}
                            <Typography fontSize={11} noWrap>{field.label}</Typography>
                          </Box>
                          <Checkbox
                            checked={field.checked || false}
                            size="small"
                            disabled={field.code === requiredField}
                            sx={{ p: 0.25 }}
                            onChange={(event) => {
                              event.stopPropagation();
                              _onStatFieldSelect(field.code, event.target.checked);
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const addMoreFieldHandler = () => {
    let fieldsCopy = [...fields];
    fieldsCopy.push({ id: uuid(), ...initialField });
    setFields(fieldsCopy);
    formikRef.current.validateForm();
  };

  const onFieldRemove = (item) => {
    let fieldsCopy = [...fields];
    fieldsCopy = fieldsCopy.filter((field) => field.id != item.id);
    setFields(fieldsCopy);
    formikRef.current.validateForm();
  };

  const onFieldTypeChange = (field, type) => {
    let fieldsCopy = [...fields];
    const index = fieldsCopy.findIndex((item) => field.id === item.id);
    fieldsCopy[index].type = type;
    setFields(fieldsCopy);
    formikRef.current.validateForm();
  };

  const onFieldTitleChange = (field, title) => {
    let fieldsCopy = [...fields];
    const index = fieldsCopy.findIndex((item) => field.id === item.id);
    fieldsCopy[index].title = title;
    setFields(fieldsCopy);
    formikRef.current.validateForm();
  };

  const onFieldMandatoryChange = (field, flag) => {
    let fieldsCopy = [...fields];
    const index = fieldsCopy.findIndex((item) => field.id === item.id);
    fieldsCopy[index].mandatory = flag;
    setFields(fieldsCopy);
    formikRef.current.validateForm();
  };
  
  // Add handler for category changes
  const onFieldCategoryChange = (field, category) => {
    console.log(`Changing category for field ${field.id} to ${category}`);
    let fieldsCopy = [...fields];
    const index = fieldsCopy.findIndex((item) => field.id === item.id);
    fieldsCopy[index].category = category;
    setFields(fieldsCopy);
    // Trigger form validation
    formikRef.current.validateForm();
  };

  // Actions handlers
  const handleAddAction = () => {
    if (!selectedActionId) return;
    const exists = selectedActions.some(a => a.actionId === selectedActionId);
    if (exists) return;
    const action = availableActions.find(a => a.id === selectedActionId);
    if (!action) return;
    const next = [
      ...selectedActions,
      {
        actionId: action.id,
        name: action.name,
        description: action.description,
        dueDays: action.defaultDueDays ?? 0,
        isNew: false,
      },
    ];
    setSelectedActions(next);
    setSelectedActionId("");
  };

  // Create a new inline action row
  const handleCreateAction = () => {
    const tempId = `new-${uuid()}`;
    setSelectedActions(prev => ([
      ...prev,
      { tempId, name: "", description: "", dueDays: 0, isNew: true }
    ]));
  };

  const handleRemoveAction = (id) => {
    setSelectedActions(prev => prev.filter(a => (a.actionId ?? a.tempId) !== id));
  };

  const handleDueDaysChange = (actionId, value) => {
    const sanitized = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0);
    setSelectedActions(prev => prev.map(a => (a.actionId === actionId || a.tempId === actionId) ? { ...a, dueDays: sanitized } : a));
  };

  const handleNewActionFieldChange = (id, field, value) => {
    setSelectedActions(prev => prev.map(a => (a.tempId === id ? { ...a, [field]: value } : a)));
  };

  return (
    <LayoutCard>
      <Formik
        initialValues={initialValues}
        onSubmit={_onSubmit}
        validationSchema={validationSchema}
        innerRef={formikRef}
      >
        {({ errors, isValid, touched, dirty, values }) => {
          const newActionsInvalid = selectedActions.some(a => a.isNew && (
            !Boolean(a.name && a.name.trim()) ||
            !Boolean(a.description && a.description.trim()) ||
            a.dueDays === '' || isNaN(Number(a.dueDays))
          ));
          return (
          <Form>
            {/* Header Section - Polished */}
            <Box sx={{
              ...cardStyle,
              mb: 3,
              backgroundColor: '#fafafa'
            }}>
              <Box sx={{ ...headerStyle, py: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                  {location.state?.group ? "Edit Service Group" : "Create Service Group"}
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={3} alignItems="flex-start">
                  <Grid item xs={12} md={4}>
                    <Field
                      name="name"
                      label="Group Name *"
                      fullWidth
                      size="small"
                      as={TextField}
                      error={Boolean(errors.name) && Boolean(touched.name)}
                      helperText={Boolean(touched.name) && errors.name}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <IconSelector
                      selectedIcon={icon}
                      selectedColor={color}
                      onIconChange={setIcon}
                      onColorChange={setColor}
                      label="Icon & Color (Optional)"
                      showPreview={true}
                      previewText={values.name || "Service Group"}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Service Target Type - Compact Toggle Design */}
            <WDBox my={3}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={4}>
                  <WDBox>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      Service Target *
                    </Typography>
                    <ToggleButtonGroup
                      value={targetType}
                      exclusive
                      onChange={(e, newValue) => newValue && setTargetType(newValue)}
                      disabled={!!location.state?.group}
                      size="small"
                      fullWidth
                      sx={{
                        '& .MuiToggleButton-root': {
                          textTransform: 'none',
                          px: 2,
                          py: 1,
                          border: '1px solid #e0e0e0',
                          '&.Mui-selected': {
                            backgroundColor: '#e0f2f1',
                            borderColor: '#009688',
                            color: '#009688',
                            '&:hover': {
                              backgroundColor: '#b2dfdb',
                            }
                          }
                        }
                      }}
                    >
                      <ToggleButton value="EMPLOYEE">
                        <PersonIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        Employee
                      </ToggleButton>
                      <ToggleButton value="ORGANIZATION">
                        <BusinessIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        Organization
                      </ToggleButton>
                      <ToggleButton value="BOTH">
                        <GroupsIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        Both
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {TARGET_TYPES.find(t => t.value === targetType)?.description}
                    </Typography>
                  </WDBox>
                </Grid>
              </Grid>
            </WDBox>

            {StaticFields()}

            <SubTitle>Form Fields</SubTitle>
            <WDBox>
              {fields.map((item) => (
                <ServiceField
                  key={item.id}
                  item={item}
                  onRemove={onFieldRemove}
                  onFieldTypeChange={onFieldTypeChange}
                  onFieldTitleChange={onFieldTitleChange}
                  onFieldMandatoryChange={onFieldMandatoryChange}
                  onFieldCategoryChange={onFieldCategoryChange}
                />
              ))}
              <WDBox display="flex" alignItems="center" justifyContent="center">
                <Button variant="text" onClick={addMoreFieldHandler}>
                  Add More Fields
                </Button>
              </WDBox>
            </WDBox>

            {/* Preview section removed as requested */}

            {/* Actions Section */}
            <SubTitle>Actions</SubTitle>
            <Box sx={cardStyle} my={2}>
              <Box sx={headerStyle}>
                <WDTypography fontSize={16} variant="h6" color="white">
                  Add Actions to Service Group
                </WDTypography>
              </Box>
              <Box sx={cardContentStyle}>
              <Grid container spacing={2} alignItems="center">
                {/* Select Action dropdown */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="action-select-label">Select Action</InputLabel>
                    <Select
                      labelId="action-select-label"
                      label="Select Action"
                      value={selectedActionId}
                      onChange={(e) => setSelectedActionId(e.target.value)}
                    >
                      {availableActions.map((a) => (
                        <MenuItem
                          key={a.id}
                          value={a.id}
                          disabled={selectedActions.some(sa => sa.actionId === a.id)}
                        >
                          {a.name} — {a.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Buttons */}
                <Grid item xs={12} md={6}>
                  <Box display="flex" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={handleAddAction}
                      disabled={!selectedActionId}
                      sx={{
                        textTransform: "none",
                        minWidth: 130,
                        color: "black !important",
                        height: "40px" // aligns with Select
                      }}
                    >
                      Add Action
                    </Button>

                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleCreateAction}
                      sx={{
                        textTransform: "none",
                        color: "black !important",
                        minWidth: 150,
                        height: "40px",
                        borderWidth: 2,         // makes border darker
                        fontWeight: "bold"      // improves readability
                      }}
                    >
                      Create Action
                    </Button>
                  </Box>
                </Grid>
              </Grid>


                {selectedActions.length > 0 && (
                  <Box mt={3}>
                    <Grid container spacing={2}>
                      {selectedActions.map((a) => (
                        <Grid item xs={12} key={a.isNew ? a.tempId : a.actionId}>
                          <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} md={3}>
                                <TextField
                                  label={a.isNew ? "Name (New Action)" : "Name"}
                                  fullWidth
                                  size="small"
                                  value={a.name}
                                  disabled={!a.isNew}
                                  required={a.isNew}
                                  error={Boolean(newActionsAttemptedSubmit && a.isNew && !Boolean(a.name && a.name.trim()))}
                                  helperText={newActionsAttemptedSubmit && a.isNew && !Boolean(a.name && a.name.trim()) ? "Name is required" : ""}
                                  onChange={(e) => a.isNew && handleNewActionFieldChange(a.tempId, 'name', e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} md={5}>
                                <TextField
                                  label="Description"
                                  fullWidth
                                  size="small"
                                  value={a.description}
                                  disabled={!a.isNew}
                                  required={a.isNew}
                                  error={Boolean(newActionsAttemptedSubmit && a.isNew && !Boolean(a.description && a.description.trim()))}
                                  helperText={newActionsAttemptedSubmit && a.isNew && !Boolean(a.description && a.description.trim()) ? "Description is required" : ""}
                                  onChange={(e) => a.isNew && handleNewActionFieldChange(a.tempId, 'description', e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} md={2}>
                                <TextField
                                  label="Due Days"
                                  type="number"
                                  fullWidth
                                  size="small"
                                  inputProps={{ min: 0 }}
                                  value={a.dueDays}
                                  required
                                  error={Boolean(newActionsAttemptedSubmit && a.isNew && (a.dueDays === '' || isNaN(Number(a.dueDays))))}
                                  helperText={newActionsAttemptedSubmit && a.isNew && (a.dueDays === '' || isNaN(Number(a.dueDays))) ? "Due days is required" : ""}
                                  onChange={(e) => handleDueDaysChange(a.isNew ? a.tempId : a.actionId, e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} md={2}>
                                <IconButton color="error" onClick={() => handleRemoveAction(a.isNew ? a.tempId : a.actionId)} aria-label="remove action">
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Box>
            </Box>

            <WDBox display="flex" justifyContent="flex-end" pt={3}>
              <WDButton variant="gradient" disabled={!dirty || !isValid || newActionsInvalid} type="submit">
                {location.state?.group ? "Update Service Group" : "Create Service Group"}
              </WDButton>
            </WDBox>
          </Form>
          );
        }}
      </Formik>
    </LayoutCard>
  );
};

export default NewServiceGroup;
